import { useState } from "react";
import { useContextAwareGetProjectById, useContextAwareGetProjectTotals, useGetClimateByIdQuery, useGetMoistureByIdQuery, useGetSoilTypeByIdQuery, useGetCountryByIdQuery } from "@hooks/index";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { selectCurrentProject } from "@features/project/projectSlice";
import { setCompareProjectId } from "@features/builder/builderSlice";
import { NoteButton } from "@components/tabsButtons/TabsButtons";
import Button from "@components/button/Button";
import TranslatableText from "@components/translations/TranslatableText";
import useErrorMessage from "@utils/useErrorMessage";
import DownloadReportButtons from "./DownloadReportButton";
import ActivitiesCheckboxes from "./ActivitiesCheckboxes";
import DescriptionBlock from "./DescriptionBlock";
import BalanceByStatus from "@components/graphs/BalanceByStatus";
import CarbBalanceByYear from "@components/graphs/CarbBalanceByYear";
import CO2ByActivityBarChart from "@components/graphs/CO2ByActivityBarChart";
import BalanceCostRatio from "@components/graphs/BalanceCostRatio";
import TotalCO2Generated from "./TotalCO2Generated";
import InventoryResults from "./InventoryResults";
import MeanSpeciesAbundance from "./msa/MeanSpeciesAbundance";
import ProjectContentHeader from "../../ProjectContentHeader";
import type { ResultsTab } from "@app/features/builder/builderTypes";
import OpenMapButton from "../../OpenMapButton";

interface ProjectResultsProps {
    projectId?: string;
    isCompare?: boolean;
    resultsTab?: ResultsTab;
}

// B-intact specific results component (placeholder for now)
const BIntactProjectResults = ({ projectId, isCompare }: ProjectResultsProps) => {
    const {
        data: project,
        isError,
        error,
        isLoading
    } = useContextAwareGetProjectById(projectId || "", { refetchOnMountOrArgChange: true });
    const { data: projectTotals } = useContextAwareGetProjectTotals(projectId || "", { refetchOnMountOrArgChange: true });
    const { errorMsg } = useErrorMessage({ error, isError });

    // Fetch related entities by ID
    const { data: countryData } = useGetCountryByIdQuery(project?.country ?? 0, { skip: !project?.country });
    const { data: climateData } = useGetClimateByIdQuery(project?.climate ?? 0, { skip: !project?.climate });
    const { data: moistureData } = useGetMoistureByIdQuery(project?.moisture ?? 0, { skip: !project?.moisture });
    const { data: soilTypeData } = useGetSoilTypeByIdQuery(project?.soil_type ?? 0, { skip: !project?.soil_type });

    const { project: currentProject } = useAppSelector(selectCurrentProject);
    const isRenderingMainProject = projectId === currentProject?.id;

    if (errorMsg) {
        return (
            <div>
                <div className="module-group">
                    <span className="error fs-13 pb-2">
                        {errorMsg || `An error occurred while fetching for project ${projectId}`}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <section>
            <div className="module-group d-flex">
                <ProjectContentHeader project={project} includeNotes={isRenderingMainProject} title="project.project_results" />
                <div className="open-map-buttons-row d-flex gap-8 py-2">
                    <OpenMapButton mode="baseline" />
                    <OpenMapButton mode="project" />
                </div>

                <div className="d-flex f-column py-2">
                    <div className="d-flex mb-1 pb-1 fs-13 lh-1 project-results-dc description-container">
                        <DescriptionBlock
                            country={countryData?.name}
                            climate={climateData?.name}
                            moisture={moistureData?.name}
                            soilType={soilTypeData?.name}
                            implDuration={project?.implementation_years}
                            capDuration={project?.capitalization_years}
                            isLoading={isLoading}
                            totalHectares={projectTotals?.total_hectares ? Math.round(projectTotals?.total_hectares) : undefined}
                            totalCatch={projectTotals?.total_catch?.w ? Math.round(projectTotals?.total_catch?.w) : undefined}
                            totalLivestock={projectTotals?.total_livestock?.w ? Math.round(projectTotals?.total_livestock?.w) : undefined}
                        />
                    </div>

                    {/* B-intact specific content will go here */}
                    <MeanSpeciesAbundance projectId={projectId} />
                </div>
            </div>
        </section>
    );
};

// Exact tool results component (existing functionality)
const ExactProjectResults = ({ projectId, isCompare }: ProjectResultsProps) => {
    const dispatch = useAppDispatch();
    const [selectedActivities, setSelectedActivities] = useState<number[]>();
    const handleActivitiesRefresh = (activities: number[]) => {
        setSelectedActivities(activities);
    };

    const {
        data: project,
        isError,
        error,
        isLoading
    } = useContextAwareGetProjectById(projectId || "", { refetchOnMountOrArgChange: true });
    const { data: projectTotals } = useContextAwareGetProjectTotals(projectId || "", { refetchOnMountOrArgChange: true });
    const { errorMsg } = useErrorMessage({ error, isError });

    const { project: currentProject } = useAppSelector(selectCurrentProject);
    const isRenderingMainProject = projectId === currentProject?.id;

    // Fetch related entities by ID
    const { data: countryData } = useGetCountryByIdQuery(project?.country ?? 0, { skip: !project?.country });
    const { data: climateData } = useGetClimateByIdQuery(project?.climate ?? 0, { skip: !project?.climate });
    const { data: moistureData } = useGetMoistureByIdQuery(project?.moisture ?? 0, { skip: !project?.moisture });
    const { data: soilTypeData } = useGetSoilTypeByIdQuery(project?.soil_type ?? 0, { skip: !project?.soil_type });

    if (errorMsg) {
        return (
            <section>
                <div className="module-group">
                    <span className="error fs-13 pb-2">
                        {errorMsg || `An error occurred while fetching for project ${projectId}`}
                    </span>
                </div>
            </section>
        );
    }

    return (
        <section>
            <div className="module-group d-flex">
                <div className="d-flex justify-content-between align-items-end mb-2 pb-1">
                    <div className="pos-relative">
                        <h2 className="fs-14 ff-medium-ext module-header w-fit">
                            <TranslatableText translationKey="project.project_results" />
                        </h2>
                        {isRenderingMainProject &&
                            <div className="pos-absolute d-flex align-items-center h-100" style={{ top: 0, left: "98%" }}>
                                <NoteButton />
                            </div>
                        }
                        <div className="module-number header-number ff-light-ext">{project?.id}</div>
                    </div>
                    {isCompare && (
                        <Button
                            onClick={() => dispatch(setCompareProjectId(""))}
                            className="fs-12 ff-medium compare-change-btn"
                        >
                            <TranslatableText translationKey="project.change_project" />
                        </Button>
                    )}
                </div>

                <div className="d-flex f-column pb-2">
                    <div className="d-flex mb-1 pb-1 fs-13 lh-1 project-results-dc description-container">
                        <DescriptionBlock
                            country={countryData?.name}
                            climate={climateData?.name}
                            moisture={moistureData?.name}
                            soilType={soilTypeData?.name}
                            implDuration={project?.implementation_years}
                            capDuration={project?.capitalization_years}
                            isLoading={isLoading}
                            totalHectares={projectTotals?.total_hectares ? Math.round(projectTotals?.total_hectares) : undefined}
                            totalCatch={projectTotals?.total_catch?.w ? Math.round(projectTotals?.total_catch?.w) : undefined}
                            totalLivestock={projectTotals?.total_livestock?.w ? Math.round(projectTotals?.total_livestock?.w) : undefined}
                        />
                    </div>

                    <div className="d-flex gap-8">
                        <DownloadReportButtons selectedActivities={selectedActivities} projectId={project?.id ?? 0} projectName={project?.name} />
                        <ActivitiesCheckboxes onRefresh={handleActivitiesRefresh} projectId={project?.id ?? 0} />
                    </div>

                    <TotalCO2Generated
                        projectId={project?.id ?? 0}
                        selectedActivities={selectedActivities}
                    />

                    <CO2ByActivityBarChart
                        projectId={project?.id ?? 0}
                        selectedActivities={selectedActivities}
                        height={100}
                        className="mb-3"
                    />

                    <BalanceByStatus
                        projectId={project?.id ?? 0}
                        selectedActivities={selectedActivities}
                        height={350}
                        className="my-3 pe-2"
                    />

                    <CarbBalanceByYear
                        projectId={project?.id ?? 0}
                        selectedActivities={selectedActivities}
                        height={350}
                        className="my-3 pe-2"
                    />

                    <BalanceCostRatio
                        projectId={project?.id ?? 0}
                        selectedActivities={selectedActivities}
                    />

                    <InventoryResults
                        projectId={project?.id ?? 0}
                        selectedActivities={selectedActivities}
                    />
                </div>
            </div>
        </section>
    );
};

// Main ProjectResults component with conditional rendering
export const ProjectResults = ({ projectId, isCompare, resultsTab = "ghg" }: ProjectResultsProps) => {
    const activeTab = isCompare ? "ghg" : resultsTab;

    return activeTab === "ghg" ? (
        <ExactProjectResults projectId={projectId} isCompare={isCompare} />
    ) : (
        <BIntactProjectResults projectId={projectId} isCompare={isCompare} />
    );
};

export default ProjectResults; 